import { useEffect, useMemo, useRef } from 'react';
import { cloneDeep, isEqual } from 'lodash-es';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import { useRhinoContext } from '../RhinoContext';
import { useBaseOwnerId } from './owner';
import type { Scalar } from '../analytics/sanitize';

import { analyticsId as id } from '../analytics/client';
type Track = (name: string, properties: Record<string, Scalar>) => void;

// Only explicitly constructed scalars cross this boundary. Never pass query
// parameters, form values, records, validation messages or exception objects.
export class FormAnalytics {
  private attemptId = id();
  private viewed = false;
  private startedAt: number | undefined;
  private completed = false;

  constructor(private readonly track: Track) {}

  private emit(name: string, properties: Record<string, Scalar> = {}) {
    this.track(name, { attempt_id: this.attemptId, ...properties });
  }

  view() {
    if (this.viewed) return;
    this.viewed = true;
    this.emit('form.viewed');
  }

  start() {
    if (this.completed) {
      this.attemptId = id();
      this.startedAt = undefined;
      this.completed = false;
      this.viewed = false;
    }
    this.view();
    if (this.startedAt !== undefined) return;
    this.startedAt = Date.now();
    this.emit('form.started');
  }

  submit() {
    this.start();
    const submissionId = id();
    this.emit('form.submitted', { submission_id: submissionId });
    return submissionId;
  }

  invalid(submissionId: string, errorCount: number) {
    this.emit('form.validation_failed', {
      submission_id: submissionId,
      error_count: errorCount
    });
  }

  // Runs around the mutation function, before onSuccess/onError callbacks.
  // This preserves caller callbacks, default invalidation and unmount saves.
  async run<T>(save: () => Promise<T>): Promise<T> {
    this.start();
    const attemptId = this.attemptId;
    const startedAt = this.startedAt!;
    const saveId = id();
    const properties = { attempt_id: attemptId, save_id: saveId };
    this.track('form.save_started', properties);
    try {
      const result = await save();
      this.track('form.save_succeeded', {
        ...properties,
        elapsed_ms: Math.max(0, Date.now() - startedAt)
      });
      if (this.attemptId === attemptId) this.completed = true;
      return result;
    } catch (error) {
      this.track('form.save_failed', properties);
      throw error;
    }
  }
}

export const useFormAnalytics = (
  model: string,
  action: 'create' | 'edit',
  recordId?: string | number
) => {
  const { analyticsClient } = useRhinoContext();
  const owner = useBaseOwnerId();
  return useMemo(
    () => {
      const track = analyticsClient.capture(owner);
      return new FormAnalytics((name, properties) => {
        if (analyticsClient.enabled) {
          track(name, {
            schema_version: 1,
            model,
            action,
            ...properties
          });
        }
      });
    },
    // A reused modal gets a new attempt when its record or organization changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps, @eslint-react/exhaustive-deps
    [analyticsClient, model, action, recordId, owner]
  );
};

export const useTrackedForm = <T extends FieldValues>(
  methods: UseFormReturn<T>,
  analytics: FormAnalytics,
  ready: boolean
): UseFormReturn<T> => {
  const { subscribe, handleSubmit, getValues } = methods;
  useEffect(() => {
    if (!ready) return;
    analytics.view();
    let previousValues = cloneDeep(getValues());
    return subscribe({
      formState: { isDirty: true, values: true },
      callback: ({ isDirty, values }) => {
        if (isDirty && !isEqual(previousValues, values)) analytics.start();
        previousValues = cloneDeep(values);
      }
    });
  }, [analytics, ready, subscribe, getValues]);

  const trackedSubmit = useMemo<UseFormReturn<T>['handleSubmit']>(
    () => (valid, invalid) => (event) => {
      const submissionId = analytics.submit();
      return handleSubmit(valid, (errors, e) => {
        analytics.invalid(submissionId, Object.keys(errors).length);
        return invalid?.(errors, e);
      })(event);
    },
    [analytics, handleSubmit]
  );
  return { ...methods, handleSubmit: trackedSubmit };
};

interface ListState {
  search: string;
  filter: Record<string, unknown>;
  fullFilter: Record<string, unknown>;
  geospatial: Record<string, unknown>;
  order: string[];
  limit: number;
  offset: number;
}
interface ListResult {
  isFetching: boolean;
  isPlaceholderData: boolean;
  isSuccess: boolean;
  isError: boolean;
  total: number | null;
  results: unknown[] | null;
}

export const useIndexAnalytics = (
  model: string,
  state: ListState,
  filterCount: number,
  query: ListResult,
  resetOffset: number,
  surface = 'unspecified'
) => {
  const { analyticsClient } = useRhinoContext();
  const owner = useBaseOwnerId();
  const previousRef = useRef<
    | {
        model: string;
        surface: string;
        owner: typeof owner;
        state: ListState;
        listId: string;
        queryId: string;
        reported: boolean;
        startedAt: number;
        pendingReset: boolean;
        track: ReturnType<typeof analyticsClient.capture>;
      }
    | undefined
  >(undefined);

  useEffect(() => {
    if (!analyticsClient.enabled) return;
    const last = previousRef.current;
    const fresh =
      !last ||
      last.model !== model ||
      last.owner !== owner ||
      last.surface !== surface;
    const changed = fresh || !isEqual(last.state, state);
    const finishingReset =
      !fresh &&
      last.pendingReset &&
      state.offset === resetOffset &&
      isEqual({ ...last.state, offset: resetOffset }, state);
    const pendingReset =
      !fresh &&
      state.offset !== resetOffset &&
      (last.state.search !== state.search ||
        !isEqual(last.state.filter, state.filter) ||
        !isEqual(last.state.geospatial, state.geospatial) ||
        last.state.limit !== state.limit);
    const properties = {
      schema_version: 1,
      model,
      surface,
      has_search: state.search.length > 0,
      filter_count: filterCount,
      sort_count: state.order.length,
      page:
        Math.floor((pendingReset ? resetOffset : state.offset) / state.limit) +
        1,
      page_size: state.limit
    };
    if (changed) {
      const current = {
        model,
        surface,
        owner,
        state,
        listId: fresh ? id() : last.listId,
        queryId: finishingReset ? last.queryId : id(),
        reported: false,
        startedAt: finishingReset ? last.startedAt : Date.now(),
        pendingReset,
        track: fresh ? analyticsClient.capture(owner) : last.track
      };
      previousRef.current = current;
      const context = {
        ...properties,
        list_id: current.listId,
        query_id: current.queryId
      };
      if (!finishingReset) current.track('list.query_started', context);
      if (fresh) current.track('list.viewed', context);
      else {
        if (last.state.search !== state.search)
          current.track('list.searched', context);
        if (!isEqual(last.state.filter, state.filter))
          current.track('list.filtered', context);
        if (!isEqual(last.state.geospatial, state.geospatial))
          current.track('list.geospatial_changed', context);
        if (!isEqual(last.state.order, state.order))
          current.track('list.sorted', context);
        if (last.state.limit !== state.limit)
          current.track('list.page_size_changed', context);
        // Filter/search changes reset the offset; that is not a pagination action.
        if (
          last.state.offset !== state.offset &&
          !(last.pendingReset && state.offset === resetOffset) &&
          last.state.search === state.search &&
          isEqual(last.state.filter, state.filter) &&
          isEqual(last.state.geospatial, state.geospatial) &&
          last.state.limit === state.limit
        )
          current.track('list.paged', context);
      }
    }
    const current = previousRef.current!;
    if (
      current.pendingReset ||
      current.reported ||
      query.isFetching ||
      query.isPlaceholderData
    )
      return;
    if (!query.isSuccess && !query.isError) return;
    current.reported = true;
    current.track(query.isError ? 'list.load_failed' : 'list.results_viewed', {
      ...properties,
      list_id: current.listId,
      query_id: current.queryId,
      elapsed_ms: Math.max(0, Date.now() - current.startedAt),
      ...(query.isSuccess
        ? {
            result_count: query.results?.length ?? 0,
            total_count: query.total ?? 0
          }
        : {})
    });
  }, [
    analyticsClient,
    model,
    owner,
    state,
    filterCount,
    query,
    resetOffset,
    surface
  ]);
};
